import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    FormControlLabel,
    Checkbox,
    Container,
    Alert,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    FormGroup,
    FormLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    SupportAgent as SupportIcon,
    Phone as PhoneIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { awsUserService } from '../../services/aws-user-service';
import { isValidGSTN, getStateCode } from '../../utils/gstHelpers';
import AnimatedLoader from '../../components/common/AnimatedLoader';

const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const businessDetailsValidationSchema = Yup.object({
    businessName: Yup.string()
        .required('Business name is required')
        .min(2, 'Business name must be at least 2 characters'),
    entityType: Yup.string()
        .required('Entity type is required'),
    natureOfBusiness: Yup.array()
        .of(Yup.string())
        .min(1, 'Please select at least one nature of business')
        .required('Nature of business is required'),
    manufacturingDetails: Yup.string().when('natureOfBusiness', {
        is: (val: string[]) => val && val.includes('manufacturing'),
        then: (schema) => schema.required('Please specify what you manufacture'),
        otherwise: (schema) => schema.notRequired()
    }),
    serviceDetails: Yup.string().when('natureOfBusiness', {
        is: (val: string[]) => val && val.includes('services'),
        then: (schema) => schema.required('Please specify what services you provide'),
        otherwise: (schema) => schema.notRequired()
    }),
    tradingDetails: Yup.string().when('natureOfBusiness', {
        is: (val: string[]) => val && val.includes('trader'),
        then: (schema) => schema.required('Please specify what you trade'),
        otherwise: (schema) => schema.notRequired()
    }),
    socialMediaDetails: Yup.string().when('natureOfBusiness', {
        is: (val: string[]) => val && val.includes('social_media'),
        then: (schema) => schema.required('Please specify your platform/handle'),
        otherwise: (schema) => schema.notRequired()
    }),
    commencementDate: Yup.date()
        .required('Commencement date is required')
        .max(new Date(), 'Commencement date cannot be in the future'),
    turnover: Yup.string()
        .required('Business turnover is required'),
    officeAddressLine1: Yup.string()
        .required('Office address is required')
        .min(5, 'Address must be at least 5 characters'),
    officeCity: Yup.string()
        .required('City is required')
        .min(2, 'City must be at least 2 characters'),
    officeState: Yup.string()
        .required('State is required'),
    officePincode: Yup.string()
        .required('Pincode is required')
        .matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
    authorizedRepName: Yup.string()
        .required('Authorized representative name is required')
        .min(2, 'Name must be at least 2 characters'),
    authorizedRepContact: Yup.string()
        .required('Contact number is required')
        .matches(/^[0-9]{10}$/, 'Contact must be 10 digits'),
    numberOfUsers: Yup.string()
        .required('Please select number of users'),
    gstRegistered: Yup.string()
        .required('Please select GST registration status')
        .oneOf(['registered', 'not_registered'], 'Invalid GST registration status'),
    gstn: Yup.string()
        .when('gstRegistered', {
            is: 'registered',
            then: (schema) => schema
                .required('GSTN is required for registered businesses')
                .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Enter valid GSTN')
                .test('state-code-match', 'GSTN state code does not match selected state', function (value) {
                    if (!value) return true;
                    const { officeState } = this.parent;
                    if (!officeState) return true;

                    const expectedStateCode = getStateCode(officeState);
                    if (!expectedStateCode) return true;

                    return value.startsWith(expectedStateCode);
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    additionalPlaces: Yup.array().when('hasAdditionalPlaces', {
        is: true,
        then: (schema) => schema.of(
            Yup.object().shape({
                addressLine1: Yup.string().required('Address is required'),
                city: Yup.string().required('City is required'),
                state: Yup.string().required('State is required'),
                pincode: Yup.string().required('Pincode is required').matches(/^[0-9]{6}$/, 'Invalid pincode'),
                gstn: Yup.string()
                    .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Enter valid GSTN')
                    .test('additional-state-code', 'GSTN state code must match state', function (value) {
                        if (!value) return true;
                        const { state } = this.parent;
                        if (!state) return true;
                        const code = getStateCode(state);
                        return code ? value.startsWith(code) : true;
                    })
                    .nullable()
            })
        ),
        otherwise: (schema) => schema.notRequired()
    })
});

const CompleteProfile: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, updateUserProfile, userProfile, recoverStaffProfile } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [supportOpen, setSupportOpen] = useState(false);
    const [supportSubject, setSupportSubject] = useState('');
    const [supportMessage, setSupportMessage] = useState('');
    const recoveryAttempted = useRef(false);

    // Redirect if profile is already completed OR user is a staff member
    useEffect(() => {
        if (userProfile?.setupCompleted || userProfile?.tenantId) {
            navigate('/dashboard');
            return;
        }

        // If userProfile is null but user is logged in, try to recover as staff
        // recoverStaffProfile() calls setUserProfile() internally, which triggers
        // this useEffect to re-run with the new profile, hitting the check above.
        if (!userProfile && currentUser && !recoveryAttempted.current) {
            recoveryAttempted.current = true;
            recoverStaffProfile().catch((e) => {
                console.warn('CompleteProfile: recoverStaffProfile failed', e);
            });
        }
    }, [userProfile, currentUser, navigate]);


    const formik = useFormik({
        initialValues: {
            businessName: '',
            entityType: '',
            natureOfBusiness: [] as string[],
            manufacturingDetails: '',
            serviceDetails: '',
            tradingDetails: '',
            socialMediaDetails: '',
            commencementDate: '',
            turnover: '',
            officeAddressLine1: '',
            officeAddressLine2: '',
            officeCity: '',
            officeState: '',
            officePincode: '',
            gstRegistered: 'not_registered',
            gstn: '',
            authorizedRepName: '',
            authorizedRepContact: '',
            numberOfUsers: '',
            hasAdditionalPlaces: false,
            additionalPlaces: [],
        },
        validationSchema: businessDetailsValidationSchema,
        onSubmit: async (values) => {
            if (!currentUser) return;

            setIsSubmitting(true);
            setError(null);

            try {
                // Construct the nature of business with details
                const enrichedNatureOfBusiness = values.natureOfBusiness.map(type => {
                    let details = '';
                    switch (type) {
                        case 'manufacturing':
                            details = values.manufacturingDetails;
                            break;
                        case 'services':
                            details = values.serviceDetails;
                            break;
                        case 'trader':
                            details = values.tradingDetails;
                            break;
                        case 'social_media':
                            details = values.socialMediaDetails;
                            break;
                    }
                    return {
                        type,
                        details
                    };
                });

                const businessDetails = {
                    businessName: values.businessName,
                    entityType: values.entityType,
                    natureOfBusiness: enrichedNatureOfBusiness, // Store as array of objects
                    commencementDate: values.commencementDate,
                    turnover: values.turnover,
                    gstRegistered: values.gstRegistered,
                    gstn: values.gstRegistered === 'registered' ? values.gstn : null,
                    officeAddress: {
                        line1: values.officeAddressLine1,
                        line2: values.officeAddressLine2 || '',
                        city: values.officeCity,
                        state: values.officeState,
                        pincode: values.officePincode,
                    },
                    hasAdditionalPlaces: values.hasAdditionalPlaces,
                    additionalPlaces: values.hasAdditionalPlaces ? values.additionalPlaces : [],
                    authorizedRepresentative: {
                        name: values.authorizedRepName,
                        contact: values.authorizedRepContact,
                    },
                    numberOfUsers: values.numberOfUsers,
                };

                await awsUserService.updateUserProfile(currentUser.uid, {
                    businessDetails,
                    setupCompleted: true,
                });

                // Update local profile context
                await updateUserProfile({ setupCompleted: true });

                console.log('Profile completed successfully!');
                navigate('/dashboard');
            } catch (err: any) {
                console.error('Error saving profile:', err);
                setError(err.message || 'Failed to save profile details. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const handleNatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        const currentNature = formik.values.natureOfBusiness;
        if (checked) {
            formik.setFieldValue('natureOfBusiness', [...currentNature, value]);
        } else {
            formik.setFieldValue('natureOfBusiness', currentNature.filter((item) => item !== value));
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="medium">
                    Complete Profile
                </Typography>
                <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<SupportIcon />}
                    onClick={() => setSupportOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Need Help?
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Dialog open={supportOpen} onClose={() => setSupportOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Contact Support</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Facing issues with onboarding? Send us a message and our support team will get back to you shortly.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="support-subject"
                        label="Subject"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={supportSubject}
                        onChange={(e) => setSupportSubject(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        id="support-message"
                        label="Message"
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={supportMessage}
                        onChange={(e) => setSupportMessage(e.target.value)}
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<PhoneIcon />}
                            href="tel:+916363154142"
                        >
                            Call Us
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            href="https://wa.me/916363154142"
                            target="_blank"
                        >
                            WhatsApp
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSupportOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            // Mock sending message
                            console.log('Support Message:', { subject: supportSubject, message: supportMessage });
                            setSupportOpen(false);
                            setSupportSubject('');
                            setSupportMessage('');
                            alert('Message sent to support team!');
                        }}
                        variant="contained"
                    >
                        Send Message
                    </Button>
                </DialogActions>
            </Dialog>

            <form onSubmit={formik.handleSubmit}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Business Information
                </Typography>

                <TextField
                    fullWidth
                    id="businessName"
                    name="businessName"
                    label="Business Name"
                    variant="outlined"
                    margin="normal"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                    helperText={formik.touched.businessName && formik.errors.businessName}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="normal" error={formik.touched.entityType && Boolean(formik.errors.entityType)}>
                            <InputLabel>Entity Type</InputLabel>
                            <Select
                                id="entityType"
                                name="entityType"
                                value={formik.values.entityType}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Entity Type"
                            >
                                <MenuItem value="proprietorship">Proprietorship</MenuItem>
                                <MenuItem value="partnership">Partnership</MenuItem>
                                <MenuItem value="pvt_ltd">Private Limited (Pvt Ltd)</MenuItem>
                                <MenuItem value="public_ltd">Public Limited</MenuItem>
                                <MenuItem value="llp">Limited Liability Partnership (LLP)</MenuItem>
                                <MenuItem value="opc">One Person Company (OPC)</MenuItem>
                                <MenuItem value="section_8">Section 8 Company</MenuItem>
                                <MenuItem value="trust">Trust</MenuItem>
                                <MenuItem value="society">Society</MenuItem>
                                <MenuItem value="huf">Hindu Undivided Family (HUF)</MenuItem>
                            </Select>
                            {formik.touched.entityType && formik.errors.entityType && (
                                <FormHelperText>{formik.errors.entityType}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset" margin="normal" error={formik.touched.natureOfBusiness && Boolean(formik.errors.natureOfBusiness)} fullWidth>
                            <FormLabel component="legend">Nature of Business</FormLabel>
                            <FormGroup row>
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.natureOfBusiness.includes('manufacturing')} onChange={handleNatureChange} value="manufacturing" />}
                                    label="Manufacturing"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.natureOfBusiness.includes('services')} onChange={handleNatureChange} value="services" />}
                                    label="Services"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.natureOfBusiness.includes('trader')} onChange={handleNatureChange} value="trader" />}
                                    label="Trader"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={formik.values.natureOfBusiness.includes('social_media')} onChange={handleNatureChange} value="social_media" />}
                                    label="Social Media"
                                />
                            </FormGroup>
                            {formik.touched.natureOfBusiness && formik.errors.natureOfBusiness && (
                                <FormHelperText>{formik.errors.natureOfBusiness as string}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Conditional Detail Fields */}
                {formik.values.natureOfBusiness.includes('manufacturing') && (
                    <TextField
                        fullWidth
                        id="manufacturingDetails"
                        name="manufacturingDetails"
                        label="What do you manufacture?"
                        variant="outlined"
                        margin="normal"
                        value={formik.values.manufacturingDetails}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.manufacturingDetails && Boolean(formik.errors.manufacturingDetails)}
                        helperText={formik.touched.manufacturingDetails && formik.errors.manufacturingDetails}
                    />
                )}
                {formik.values.natureOfBusiness.includes('services') && (
                    <TextField
                        fullWidth
                        id="serviceDetails"
                        name="serviceDetails"
                        label="What services do you provide?"
                        variant="outlined"
                        margin="normal"
                        value={formik.values.serviceDetails}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.serviceDetails && Boolean(formik.errors.serviceDetails)}
                        helperText={formik.touched.serviceDetails && formik.errors.serviceDetails}
                    />
                )}
                {formik.values.natureOfBusiness.includes('trader') && (
                    <TextField
                        fullWidth
                        id="tradingDetails"
                        name="tradingDetails"
                        label="What do you trade?"
                        variant="outlined"
                        margin="normal"
                        value={formik.values.tradingDetails}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.tradingDetails && Boolean(formik.errors.tradingDetails)}
                        helperText={formik.touched.tradingDetails && formik.errors.tradingDetails}
                    />
                )}
                {formik.values.natureOfBusiness.includes('social_media') && (
                    <TextField
                        fullWidth
                        id="socialMediaDetails"
                        name="socialMediaDetails"
                        label="Social Media Platform/Handle?"
                        variant="outlined"
                        margin="normal"
                        value={formik.values.socialMediaDetails}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.socialMediaDetails && Boolean(formik.errors.socialMediaDetails)}
                        helperText={formik.touched.socialMediaDetails && formik.errors.socialMediaDetails}
                    />
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="commencementDate"
                            name="commencementDate"
                            label="Commencement of Business"
                            type="date"
                            variant="outlined"
                            margin="normal"
                            value={formik.values.commencementDate}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.commencementDate && Boolean(formik.errors.commencementDate)}
                            helperText={formik.touched.commencementDate && formik.errors.commencementDate}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="normal" error={formik.touched.turnover && Boolean(formik.errors.turnover)}>
                            <InputLabel>Business Turnover</InputLabel>
                            <Select
                                id="turnover"
                                name="turnover"
                                value={formik.values.turnover}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Business Turnover"
                            >
                                <MenuItem value="less_than_50_lakhs">Less than ₹50 Lakhs</MenuItem>
                                <MenuItem value="50_lakhs_to_10_crore">₹50 Lakhs to ₹10 Crore</MenuItem>
                                <MenuItem value="more_than_10_crore">More than ₹10 Crore</MenuItem>
                            </Select>
                            {formik.touched.turnover && formik.errors.turnover && (
                                <FormHelperText>{formik.errors.turnover}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                    GST Information
                </Typography>

                <FormControl fullWidth margin="normal" error={formik.touched.gstRegistered && Boolean(formik.errors.gstRegistered)}>
                    <InputLabel>GST Registration Status</InputLabel>
                    <Select
                        id="gstRegistered"
                        name="gstRegistered"
                        value={formik.values.gstRegistered}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="GST Registration Status"
                    >
                        <MenuItem value="registered">Registered</MenuItem>
                        <MenuItem value="not_registered">Not Registered</MenuItem>
                    </Select>
                    {formik.touched.gstRegistered && formik.errors.gstRegistered && (
                        <FormHelperText>{formik.errors.gstRegistered}</FormHelperText>
                    )}
                </FormControl>

                {formik.values.gstRegistered === 'registered' && (
                    <TextField
                        fullWidth
                        id="gstn"
                        name="gstn"
                        label="GSTN (GST Number)"
                        variant="outlined"
                        margin="normal"
                        placeholder="e.g., 27AAPFU0939F1ZV"
                        value={formik.values.gstn}
                        onChange={(e) => formik.setFieldValue('gstn', e.target.value.toUpperCase())}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gstn && Boolean(formik.errors.gstn)}
                        helperText={formik.touched.gstn && formik.errors.gstn ? formik.errors.gstn : undefined}
                        inputProps={{ maxLength: 15 }}
                    />
                )}

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                    Office Address
                </Typography>

                <TextField
                    fullWidth
                    id="officeAddressLine1"
                    name="officeAddressLine1"
                    label="Address Line 1"
                    variant="outlined"
                    margin="normal"
                    value={formik.values.officeAddressLine1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.officeAddressLine1 && Boolean(formik.errors.officeAddressLine1)}
                    helperText={formik.touched.officeAddressLine1 && formik.errors.officeAddressLine1}
                />

                <TextField
                    fullWidth
                    id="officeAddressLine2"
                    name="officeAddressLine2"
                    label="Address Line 2 (Optional)"
                    variant="outlined"
                    margin="normal"
                    value={formik.values.officeAddressLine2}
                    onChange={formik.handleChange}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="officeCity"
                            name="officeCity"
                            label="City"
                            variant="outlined"
                            margin="normal"
                            value={formik.values.officeCity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.officeCity && Boolean(formik.errors.officeCity)}
                            helperText={formik.touched.officeCity && formik.errors.officeCity}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={indianStates}
                            value={formik.values.officeState}
                            onChange={(event, newValue) => formik.setFieldValue('officeState', newValue || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="State"
                                    margin="normal"
                                    error={formik.touched.officeState && Boolean(formik.errors.officeState)}
                                    helperText={formik.touched.officeState && formik.errors.officeState}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="officePincode"
                            name="officePincode"
                            label="Pincode"
                            variant="outlined"
                            margin="normal"
                            value={formik.values.officePincode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.officePincode && Boolean(formik.errors.officePincode)}
                            helperText={formik.touched.officePincode && formik.errors.officePincode}
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                    Authorized Representative
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="authorizedRepName"
                            name="authorizedRepName"
                            label="Name"
                            variant="outlined"
                            margin="normal"
                            value={formik.values.authorizedRepName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.authorizedRepName && Boolean(formik.errors.authorizedRepName)}
                            helperText={formik.touched.authorizedRepName && formik.errors.authorizedRepName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="authorizedRepContact"
                            name="authorizedRepContact"
                            label="Contact Number"
                            variant="outlined"
                            margin="normal"
                            value={formik.values.authorizedRepContact}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.authorizedRepContact && Boolean(formik.errors.authorizedRepContact)}
                            helperText={formik.touched.authorizedRepContact && formik.errors.authorizedRepContact}
                        />
                    </Grid>
                </Grid>

                <FormControl fullWidth margin="normal" error={formik.touched.numberOfUsers && Boolean(formik.errors.numberOfUsers)}>
                    <InputLabel>Number of Users</InputLabel>
                    <Select
                        id="numberOfUsers"
                        name="numberOfUsers"
                        value={formik.values.numberOfUsers}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Number of Users"
                    >
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                        <MenuItem value="more_than_5">More than 5</MenuItem>
                    </Select>
                    {formik.touched.numberOfUsers && formik.errors.numberOfUsers && (
                        <FormHelperText>{formik.errors.numberOfUsers}</FormHelperText>
                    )}
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            id="hasAdditionalPlaces"
                            name="hasAdditionalPlaces"
                            checked={formik.values.hasAdditionalPlaces}
                            onChange={(e) => {
                                formik.handleChange(e);
                                if (e.target.checked && formik.values.additionalPlaces.length === 0) {
                                    formik.setFieldValue('additionalPlaces', [{ addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', gstn: '' }]);
                                } else if (!e.target.checked) {
                                    formik.setFieldValue('additionalPlaces', []);
                                }
                            }}
                        />
                    }
                    label="Has additional business places"
                    sx={{ mt: 2 }}
                />

                {formik.values.hasAdditionalPlaces && (
                    <Box sx={{ mt: 2 }}>
                        {formik.values.additionalPlaces.map((place: any, index: number) => (
                            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Additional Place #{index + 1}
                                    </Typography>
                                    {formik.values.additionalPlaces.length > 1 && (
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                const newPlaces = [...formik.values.additionalPlaces];
                                                newPlaces.splice(index, 1);
                                                formik.setFieldValue('additionalPlaces', newPlaces);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Address Line 1"
                                    variant="outlined"
                                    size="small"
                                    margin="dense"
                                    value={place.addressLine1}
                                    onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].addressLine1`, e.target.value)}
                                    error={formik.touched.additionalPlaces?.[index]?.addressLine1 && Boolean((formik.errors.additionalPlaces?.[index] as any)?.addressLine1)}
                                    helperText={formik.touched.additionalPlaces?.[index]?.addressLine1 && (formik.errors.additionalPlaces?.[index] as any)?.addressLine1}
                                />

                                <TextField
                                    fullWidth
                                    label="Address Line 2 (Optional)"
                                    variant="outlined"
                                    size="small"
                                    margin="dense"
                                    value={place.addressLine2}
                                    onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].addressLine2`, e.target.value)}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="City"
                                            variant="outlined"
                                            size="small"
                                            margin="dense"
                                            value={place.city}
                                            onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].city`, e.target.value)}
                                            error={formik.touched.additionalPlaces?.[index]?.city && Boolean((formik.errors.additionalPlaces?.[index] as any)?.city)}
                                            helperText={formik.touched.additionalPlaces?.[index]?.city && (formik.errors.additionalPlaces?.[index] as any)?.city}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={indianStates}
                                            value={place.state}
                                            onChange={(event, newValue) => {
                                                formik.setFieldValue(`additionalPlaces[${index}].state`, newValue || '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="State"
                                                    margin="dense"
                                                    size="small"
                                                    error={formik.touched.additionalPlaces?.[index]?.state && Boolean((formik.errors.additionalPlaces?.[index] as any)?.state)}
                                                    helperText={formik.touched.additionalPlaces?.[index]?.state && (formik.errors.additionalPlaces?.[index] as any)?.state}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Pincode"
                                            variant="outlined"
                                            size="small"
                                            margin="dense"
                                            value={place.pincode}
                                            onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].pincode`, e.target.value)}
                                            error={formik.touched.additionalPlaces?.[index]?.pincode && Boolean((formik.errors.additionalPlaces?.[index] as any)?.pincode)}
                                            helperText={formik.touched.additionalPlaces?.[index]?.pincode && (formik.errors.additionalPlaces?.[index] as any)?.pincode}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="GSTN (Optional)"
                                            variant="outlined"
                                            size="small"
                                            margin="dense"
                                            placeholder={place.state ? `Must start with ${getStateCode(place.state) || '..'}` : ''}
                                            value={place.gstn || ''}
                                            onChange={(e) => formik.setFieldValue(`additionalPlaces[${index}].gstn`, e.target.value.toUpperCase())}
                                            error={formik.touched.additionalPlaces?.[index]?.gstn && Boolean((formik.errors.additionalPlaces?.[index] as any)?.gstn)}
                                            helperText={formik.touched.additionalPlaces?.[index]?.gstn && (formik.errors.additionalPlaces?.[index] as any)?.gstn}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const newPlaces = [...formik.values.additionalPlaces, { addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', gstn: '' }];
                                formik.setFieldValue('additionalPlaces', newPlaces);
                            }}
                            sx={{ mt: 1 }}
                        >
                            + Add Another Place
                        </Button>
                    </Box>
                )}

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        sx={{ minWidth: 200 }}
                    >
                        {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Complete Profile'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default CompleteProfile;
